import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components/macro'
import FlexibleDialog, {
  propTypes as dialogPropTypes,
} from 'components/molecules/flexible-dialog'
import Typography from 'components/molecules/typography'
import LoadingButton from 'components/atoms/loading-button'
import NewDropDownSelect from 'components/molecules/new-dropdown-select'
import NewTextInput from 'components/atoms/new-text-input'
import { media } from 'utilities/styled'
import { createThuistaxatieRequest } from 'redux/actions/data'
import PropTypes from 'prop-types'
import toast from 'utilities/toast'
import { THUISTAXATIE } from 'config/routes'

const StyledFlexibleDialog = styled(FlexibleDialog)`
  .MuiDialog-paper {
    width: ${({ theme }) => theme.sizeByFactor(75)};
  }
`

const Intro = styled(Typography)`
  margin: 0;
  white-space: pre-line;
  padding: ${({ theme }) => theme.sizings.lvl2};
`

const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ theme }) => theme.sizings.lvl4};
  padding: ${({ theme }) => theme.sizings.lvl2};
`

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.sizings.lvl2};
  padding: ${({ theme }) => theme.sizings.lvl2};

  ${media.desktop`
    grid-template-columns: repeat(6, 1fr);
  `}
`

const FormField = styled.div`
  width: 100%;

  ${media.desktop`
    &.col-2 {
      grid-column: span 2;
    }

    &.col-3 {
      grid-column: span 3;
    }
  `}
`

const HomeValuationDialog = ({ onClose, ...restProps }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [formData, setFormData] = React.useState({
    title: '',
    licenseplate: '',
    mileage: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
  })

  const TitleOptionEnum = {
    SIR: { label: t('thuistaxatie.modalOptions.salutation.sir'), value: 'sir' },
    MADAM: {
      label: t('thuistaxatie.modalOptions.salutation.madam'),
      value: 'madam',
    },
  }

  const titleOptions = Object.values(TitleOptionEnum)

  const handleChange = (field) => (value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSubmit = () => {
    setIsSubmitting(true)
    const { licenseplate, mileage, phone, email, firstName, lastName } =
      formData
    const payload = {
      licenseplate,
      mileage,
      phone,
      email,
      first_name: firstName,
      last_name: lastName,
    }

    dispatch(createThuistaxatieRequest(payload))
      .then((data) => {
        setIsSubmitting(false)
        if (data && data?.success) {
          toast.success(t('thuistaxatie.success'))
          history.push(THUISTAXATIE)
          onClose && onClose()
        }
      })
      .catch((error) => {
        console.error('Error submitting thuistaxatie:', error)
        setIsSubmitting(false)
      })
  }

  const isPhoneRequired = !formData.email
  const isEmailRequired = !formData.phone

  return (
    <StyledFlexibleDialog
      title={t('thuistaxatie.dialogTitle')}
      disableBackdropClick={isSubmitting}
      onClose={onClose}
      open
      content={
        <>
          <Intro type="ExplanationParagraph">
            {t('thuistaxatie.dialogDescription')}
          </Intro>
          <FormGrid>
            <FormField className="col-2">
              <NewDropDownSelect
                id="title"
                items={titleOptions}
                onChange={handleChange('title')}
                label={t('thuistaxatie.modalOptions.salutation.title')}
                value={formData.title}
                required
                filled
              />
            </FormField>
            <FormField className="col-2">
              <NewTextInput
                id="licenseplate"
                label={t('licenseplate')}
                onChange={handleChange('licenseplate')}
                value={formData.licenseplate}
                required
                filled
              />
            </FormField>
            <FormField className="col-2">
              <NewTextInput
                id="mileage"
                label={t('mileage')}
                onChange={handleChange('mileage')}
                value={formData.mileage}
                required
                filled
                type="number"
              />
            </FormField>
            <FormField className="col-3">
              <NewTextInput
                id="firstName"
                label={t('firstName')}
                onChange={handleChange('firstName')}
                value={formData.firstName}
                required
                filled
              />
            </FormField>
            <FormField className="col-3">
              <NewTextInput
                id="lastName"
                label={t('lastName')}
                onChange={handleChange('lastName')}
                value={formData.lastName}
                required
                filled
              />
            </FormField>
            <FormField className="col-3">
              <NewTextInput
                id="phone"
                label={t('phone')}
                onChange={handleChange('phone')}
                value={formData.phone}
                required={isPhoneRequired}
                filled
              />
            </FormField>
            <FormField className="col-3">
              <NewTextInput
                id="email"
                label={t('email')}
                onChange={handleChange('email')}
                value={formData.email}
                required={isEmailRequired}
                filled
                type="email"
              />
            </FormField>
          </FormGrid>
          <Controls>
            <LoadingButton
              level="option"
              onClick={onClose}
              text={t('cancel')}
              disabled={isSubmitting}
              noPadding
              data-test-e2e="button-cancel"
            />
            <LoadingButton
              level="cta"
              isLoading={isSubmitting}
              type="submit"
              onClick={handleSubmit}
              data-test-e2e="button-save"
            >
              {t('submit')}
            </LoadingButton>
          </Controls>
        </>
      }
      {...restProps}
      data-test-e2e="home-valuation-dialog"
    />
  )
}

HomeValuationDialog.propTypes = {
  title: PropTypes.string,
  content: PropTypes.node,
  ...dialogPropTypes,
}

export default HomeValuationDialog
